import React, { useState, useEffect, useCallback } from 'react'; 
import { 
  TrendingUp, Building, Users, Star, RefreshCw, Clock, Gift, 
  Calendar, Activity, AlertCircle, Filter, User, HelpCircle
} from 'lucide-react';

// Métricas globales de plataforma desde lealify-base (re-cableo, reemplaza esapi/ES).
import { adminMetricsApi } from '../../../api/adminMetricsApi';
import CountUp from '../../../components/ui/CountUp';
import BrandLoader from '../../../components/common/BrandLoader';

// Interfaz para el estado local
interface AdminMetrics {
  totalBusinesses: number;
  activeBusinesses: number;
  totalClients: number;
  totalVisits: number; 
  totalPointsAwarded: number; 
  activeClients: number;
  activeBranches: number;
  maleClients: number; 
  femaleClients: number; 
  unknownGenderCustomers: number;
  newClientsLast30Days: number;
  activeRewardsCount: number;
  lastUpdateDate: Date | null;
}

// Componente para métricas globales de administrador
const AdminBusinessMetrics: React.FC = () => {
  // Estado inicial
  const [metrics, setMetrics] = useState<AdminMetrics>({
    totalBusinesses: 0,
    activeBusinesses: 0,
    totalClients: 0, 
    totalVisits: 0, 
    totalPointsAwarded: 0, 
    activeClients: 0, 
    activeBranches: 0, 
    maleClients: 0, 
    femaleClients: 0,
    unknownGenderCustomers: 0, 
    newClientsLast30Days: 0,
    activeRewardsCount: 0,
    lastUpdateDate: null
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d'); 

  // --- Función fetchMetrics usando la API del Backend ---
  const fetchMetrics = useCallback(async () => { 
    setLoading(true); setError(null);
    try {
      const dataFromApi = await adminMetricsApi.globalMetrics(timeRange);
      setMetrics({
        totalClients: dataFromApi.totalClients,
        newClientsLast30Days: dataFromApi.newClientsLast30Days, 
        totalBusinesses: dataFromApi.totalBusinesses,
        activeBusinesses: dataFromApi.activeBusinesses ?? 0,
        totalVisits: dataFromApi.totalVisits || 0,
        totalPointsAwarded: dataFromApi.totalPointsAwarded || 0, 
        activeClients: dataFromApi.activeClients || 0, 
        activeBranches: dataFromApi.activeBranches || 0,
        maleClients: dataFromApi.maleClients || 0,
        femaleClients: dataFromApi.femaleClients || 0,
        unknownGenderCustomers: dataFromApi.unknownGenderCustomers || 0,
        activeRewardsCount: dataFromApi.activeRewardsCount || 0,
        lastUpdateDate: new Date()
      });
    } catch (err) {
      console.error('Error en AdminBusinessMetrics al llamar fetchOverallMetrics:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar métricas globales.');
    } finally {
      setLoading(false);
    }
  }, [timeRange]); 

  // useEffect para llamar fetchMetrics
  useEffect(() => { fetchMetrics(); }, [fetchMetrics]); 

  // Función formatear número
  const formatNumber = (num: number): string => { 
    if (num === null || num === undefined || isNaN(num)) return '0'; 
    return new Intl.NumberFormat('es-MX').format(num);
   };

  // --- Renderizado ---

  // Estado de Carga Inicial
  if (loading && metrics.totalClients === 0) { 
    return (
      <div className="bg-surface rounded-lg shadow p-6 text-center">
        <div className="flex justify-center">
          <BrandLoader size={36} />
        </div>
        <p className="text-content-3">Cargando métricas globales...</p>
      </div>
    );
  }

  // Estado de Error
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-500/10 border-l-4 border-red-400 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0"> <AlertCircle className="h-5 w-5 text-red-400" /> </div>
          <div className="ml-3">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <div className="mt-2"> <button onClick={fetchMetrics} className="text-sm font-medium text-red-700 dark:text-red-300 hover:text-red-600 dark:text-red-400 underline" > Intentar de nuevo </button> </div>
          </div>
        </div>
      </div>
    );
  }

  // Estado con Datos
  return (
    <div className="bg-surface rounded-lg shadow overflow-hidden">
      {/* Encabezado con filtro */}
      <div className="px-4 py-5 sm:px-6 border-b border-line">
        <div className="flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-content flex items-center">
            <TrendingUp className="mr-2 h-5 w-5 text-purple-500" /> Métricas Globales
          </h3>
          <div className="flex items-center space-x-2">
             <Filter className="h-4 w-4 text-content-3" />
             <select 
               value={timeRange} 
               onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')} 
               className="border-line rounded-md text-sm shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
             >
                <option value="7d">Últimos 7 días</option>
                <option value="30d">Últimos 30 días</option>
                <option value="90d">Últimos 90 días</option>
             </select>
           </div>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-content-3">
          Estadísticas agregadas de todos los negocios en la plataforma
        </p>
      </div>

      {/* Rejilla de Métricas */}
      <div className="px-4 py-5 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Negocios Registrados */}
          <div className="bg-purple-50 dark:bg-purple-500/10 p-4 rounded-lg shadow-sm border border-purple-100">
             <div className="flex items-center">
               <Building className="h-8 w-8 mr-3 text-purple-500" />
               <div>
                 <p className="text-sm font-medium text-purple-800 dark:text-purple-300">Negocios activos (30 días)</p> 
                 <p className="text-2xl font-display font-semibold text-purple-900 dark:text-purple-300"><CountUp value={metrics.activeBusinesses} format={formatNumber} /></p>
                 {/* El número grande es el de USO REAL; los registrados quedan como referencia (20-sep-2026): antes la
                     tarjeta mostraba todos los registros y decía «337» cuando operan ~65. */}
                 <p className="mt-1 text-xs text-purple-700/80 dark:text-purple-300/70" title="Con al menos una visita o una cita en los últimos 30 días, frente a todos los que se registraron alguna vez">de {formatNumber(metrics.totalBusinesses)} registrados</p> 
               </div>
             </div>
           </div>

           {/* Total Clientes */}
           <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg shadow-sm border border-blue-100">
             <div className="flex items-center">
               <Users className="h-8 w-8 mr-3 text-blue-500" />
               <div>
                 <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Total de Clientes</p>
                 <p className="text-2xl font-display font-semibold text-blue-900 dark:text-blue-300"><CountUp value={metrics.totalClients} format={formatNumber} /></p>
                 <p className="mt-1 text-xs text-accent">{formatNumber(metrics.newClientsLast30Days)} nuevos (30d)</p> 
               </div>
             </div>
             {/* Género */}
             <div className="mt-2 flex items-center text-xs text-blue-700 dark:text-blue-300 divide-x divide-blue-200">
               <div className="pr-2 inline-flex items-center" title="Hombres">
                <User className="h-3.5 w-3.5 mr-1 text-blue-500" aria-hidden="true" />
                <span className="font-medium mr-1">H:</span> {formatNumber(metrics.maleClients)}
              </div>
              <div className="px-2 inline-flex items-center" title="Mujeres">
                <User className="h-3.5 w-3.5 mr-1 text-pink-500" aria-hidden="true" />
                <span className="font-medium mr-1">M:</span> {formatNumber(metrics.femaleClients)}
              </div>
              <div className="pl-2 inline-flex items-center" title="No especificado / Otro">
                <HelpCircle className="h-3.5 w-3.5 mr-1 text-content-3" aria-hidden="true"/>
                <span className="font-medium mr-1">?:</span> {formatNumber(metrics.unknownGenderCustomers)}
              </div>
             </div>
           </div>

           {/* Visitas Registradas */}
           <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg shadow-sm border border-green-100">
             <div className="flex items-center"> <Calendar className="h-8 w-8 mr-3 text-green-500" /> <div> <p className="text-sm font-medium text-green-800 dark:text-green-300">Visitas Registradas</p> <p className="text-2xl font-display font-semibold text-green-900 dark:text-green-300"><CountUp value={metrics.totalVisits} format={formatNumber} /></p> </div> </div>
           </div>

           {/* Recompensas Activas */}
           <div className="bg-amber-50 dark:bg-amber-500/10 p-4 rounded-lg shadow-sm border border-amber-100 dark:border-amber-500/20">
             <div className="flex items-center"> 
               <Gift className="h-8 w-8 mr-3 text-amber-500" /> 
               <div> 
                 <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Recompensas Activas</p> 
                 <p className="text-2xl font-display font-semibold text-amber-900 dark:text-amber-300"><CountUp value={metrics.activeRewardsCount} format={formatNumber} /></p> 
               </div> 
             </div>
           </div>

           {/* Puntos Otorgados */}
           <div className="bg-yellow-50 dark:bg-yellow-500/10 p-4 rounded-lg shadow-sm border border-yellow-100">
             <div className="flex items-center"> <Star className="h-8 w-8 mr-3 text-yellow-500" /> <div> <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Puntos/Valor Otorgado</p> <p className="text-2xl font-display font-semibold text-yellow-900 dark:text-yellow-300"><CountUp value={metrics.totalPointsAwarded} format={formatNumber} /></p> </div> </div>
           </div>

           {/* Clientes Activos */}
           <div className="bg-teal-50 dark:bg-teal-500/10 p-4 rounded-lg shadow-sm border border-teal-100">
             <div className="flex items-center"> <Activity className="h-8 w-8 mr-3 text-teal-500" /> <div> <p className="text-sm font-medium text-teal-800 dark:text-teal-300">Clientes Activos</p> <p className="text-2xl font-display font-semibold text-teal-900 dark:text-teal-300"><CountUp value={metrics.activeClients} format={formatNumber} /></p> </div> </div>
           </div>

           {/* Sucursales Activas */}
           <div className="bg-indigo-50 dark:bg-indigo-500/10 p-4 rounded-lg shadow-sm border border-indigo-100">
             <div className="flex items-center"> <Building className="h-8 w-8 mr-3 text-indigo-500" /> <div> <p className="text-sm font-medium text-indigo-800 dark:text-indigo-300">Sucursales Activas</p> <p className="text-2xl font-display font-semibold text-indigo-900 dark:text-indigo-300"><CountUp value={metrics.activeBranches} format={formatNumber} /></p> </div> </div>
           </div>

        </div>

        {/* Timestamp Última Actualización */}
        {metrics.lastUpdateDate && (
          <div className="mt-6 flex justify-end text-xs text-content-3">
            <Clock className="h-3 w-3 mr-1" />
            <span>Última actualización: {metrics.lastUpdateDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <button onClick={fetchMetrics} className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:text-purple-300 disabled:opacity-50" disabled={loading} title="Actualizar datos" >
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBusinessMetrics;